import Bugsnag from 'core/libs/bugsnag';
import getConfig from 'next/config';
import { createRef } from 'react';
import { checkCard, createToken } from 'src/payments/handlers/api';
import {
  CardCheck,
  Chargebee3DSHandler,
  PaymentAdditionalDetails,
  PaymentProviders,
  Three3dsPaymentInformation,
} from 'src/payments/models/payments';
import Track from 'src/payments/handlers/track';
import { getLabelForPremiumDuration } from 'core/utils';

const { publicRuntimeConfig } = getConfig();

interface CbCard {
  current: {
    tokenize: (data: PaymentAdditionalDetails) => Promise<{ token: string; vaultToken: string }>;
    authorizeWith3ds: Function;
  };
}

class ChargebeeClient {
  retries = 0;

  card = <CbCard>createRef();

  paymentHandler: any;

  constructor() {
    this.loadScripts();
  }

  private static instance: ChargebeeClient;

  static getInstance(): ChargebeeClient {
    if (!ChargebeeClient.instance) {
      ChargebeeClient.instance = new ChargebeeClient();
    }
    return ChargebeeClient.instance;
  }

  private attempLoad(resolve: Function) {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://js.chargebee.com/v2/chargebee.js';
    document.head.appendChild(script);
    script.onload = () => {
      this.initiateScripts();
      resolve(true);
    };
    script.onerror = () => {
      if (this.retries < 2) {
        this.retries += 1;
        this.attempLoad(resolve);
      } else {
        this.retries = 0;
        Bugsnag.notify({
          name: 'Chargebee load fail',
          message: 'Failed to load chargebee scripts',
        });
        resolve(false);
      }
    };
  }

  private initiateScripts() {
    try {
      if (!this.initiated()) {
        window.Chargebee.init({
          site: publicRuntimeConfig.CHARGEBEE_SITE,
          publishableKey: publicRuntimeConfig.CHARGEBEE_PUBLISHABLE_KEY,
        });
      }
    } catch (e) {
      Bugsnag.notify({
        name: 'Chargebee init fail',
        message: 'Failed to initiate chargebee fields',
      });
    }
  }

  loadScripts(): Promise<boolean> {
    return new Promise((resolve) => this.attempLoad(resolve));
  }

  private async getPaymentIntent(amount: number): Promise<any> {
    return createToken({
      provider: PaymentProviders.CHARGEBEE_STRIPE,
      amount,
    });
  }

  tokenizeCard(details: PaymentAdditionalDetails) {
    return this.card.current.tokenize(details);
  }

  async loadPaymentHandler(): Promise<Chargebee3DSHandler> {
    if (!this.paymentHandler) {
      this.paymentHandler = await (window.Chargebee as any).getInstance().load3DSHandler();
    }
    return this.paymentHandler;
  }

  async performCardCheck(vaultToken: string): Promise<CardCheck> {
    try {
      await checkCard(vaultToken, PaymentProviders.CHARGEBEE_STRIPE);
      return {};
    } catch (error: any) {
      if (error.code === 'CARD_ALREADY_EXISTS') {
        Track.cardAlreadyExists(PaymentProviders.CHARGEBEE_STRIPE);
        return { skipAdd: true };
      }
      Track.cardRuleHit({
        paymentMethod: PaymentProviders.CHARGEBEE_STRIPE,
        code: error.code,
        message: error.message,
      });
      return { abort: true, message: error.message };
    }
  }

  async tokenize3DSCard({
    details,
    planID,
    planFrequency,
    amount,
    skippedBillingDetails,
  }: {
    details: PaymentAdditionalDetails;
    planID?: string;
    planFrequency?: number;
    amount: number;
    skippedBillingDetails: boolean;
  }): Promise<CardCheck> {
    try {
      const paymentHandler = await this.loadPaymentHandler();
      const cbToken = await this.tokenizeCard(details);
      // temporarily commenting out checkcard api
      // const cardResponse = await this.performCardCheck(cbToken.vaultToken);
      // if (cardResponse.abort || cardResponse.skipAdd) return cardResponse;
      const paymentIntent = await this.getPaymentIntent(amount);
      paymentHandler.setPaymentIntent(paymentIntent);
      const response: { id: string } = await new Promise((resolve, reject) => {
        const paymentInformation: Three3dsPaymentInformation = {
          cbToken: cbToken.token,
          additionalData: {
            customerBillingAddress: {
              firstName: details.firstName,
              lastName: details.lastName,
              addressLine1: details.billingAddr1,
              addressLine2: details.billingAddr2,
              city: details.billingCity,
              state: details.billingState,
              stateCode: details.billingStateCode,
              countryCode: details.billingCountry,
              zip: details.billingZip,
            },
          },
        };
        // if (
        //   planID &&
        //   planFrequency &&
        //   paymentInformation.additionalData &&
        //   !skippedBillingDetails
        // ) {
        //   paymentInformation.additionalData.plan = planID;
        //   paymentInformation.additionalData.mandate = {
        //     requireMandate: true,
        //     description: `${getLabelForPremiumDuration(planFrequency)} Subscription`,
        //   };
        // }
        paymentHandler.handleCardPayment(paymentInformation, {
          change: (intent: { active_payment_attempt?: { status: string } }, step?: string) => {
            Track.processingCard({
              paymentMethod: PaymentProviders.CHARGEBEE_STRIPE,
              paymentIntentStatus: intent?.active_payment_attempt?.status,
              step,
            });
          },
          success: (intent: { id: string }) => {
            resolve(intent);
          },
          error: (_: { id: string }, error: any) => {
            reject(error);
          },
        });
      });
      return { token: response.id };
    } catch (error: any) {
      // Errors come directly from stripe in a weird manner. Format: <error_message>;<error_code>. We only need the error message as it is the same the server gives us so we split and send this
      if (typeof error.message === 'string') {
        throw new Error(error.message.split(';')[0]);
      }
      throw new Error('Failed to read card details');
    }
  }

  async tokenize3DSCardOld(): Promise<string> {
    try {
      const paymentIntent = await createToken({
        provider: PaymentProviders.CHARGEBEE_STRIPE,
      });
      const authPaymentToken = await this.card.current.authorizeWith3ds(paymentIntent);
      return authPaymentToken.id;
    } catch (error: any) {
      // Errors come directly from stripe in a weird manner. Format: <error_message>;<error_code>. We only need the error message as it is the same the server gives us so we split and send this
      if (typeof error.message === 'string') {
        throw new Error(error.message.split(';')[0]);
      }
      throw new Error('Failed to read card details');
    }
  }

  initiated() {
    return !!window.Chargebee?.inited;
  }
}

export default ChargebeeClient;
